import { useState, useEffect, useCallback } from 'react';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';

// ── Deciding a "supplier invoice paid on a PO" Action ────────────────────────
//
// Sage has reported a supplier invoice paid, and it was recorded against a PO
// on a SOW. Some POs release a milestone to Ready to bill once the supplier is
// paid, so the PMO says which one, or that there is none, here on the card.
//
// The milestone is moved by the ordinary milestone save, the same call the
// Milestones screen makes, so ready_to_bill_date, the audit trail and the
// Ready to bill notice to finance all behave as they do there. Only then is
// the Action told which milestone it was, which closes it.

const m = (n) => n == null ? '' : `£${Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PoInvoicePaidDecision({ actionId, canDecide, onDecided }) {
  const [info, setInfo] = useState(undefined);   // undefined = loading, null = could not load
  const [milestoneId, setMilestoneId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    api.get(`actions/${actionId}/po-invoice-paid`).then(d => {
      setInfo(d);
      if (d?.milestones?.length === 1) setMilestoneId(d.milestones[0].id);
    }).catch(() => setInfo(null));
  }, [actionId]);
  useEffect(() => { load(); }, [load]);

  const decide = async (withMilestone) => {
    setBusy(true); setError('');
    try {
      // Already Ready to bill (moved on the Milestones screen, or an earlier
      // attempt that moved it and then failed to close this) needs no second move.
      const chosen = info.milestones.find(ms => ms.id === milestoneId);
      if (withMilestone && chosen?.status === 'Awaiting') await api.put(`milestones/${milestoneId}`, { status: 'Ready to bill' });
      await api.post(`actions/${actionId}/po-invoice-paid`, { milestone_id: withMilestone ? milestoneId : null, note: note.trim() || null });
      onDecided && onDecided();
    } catch (e) {
      setError(e?.message || 'That could not be saved. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (info === undefined) return <div className="text-[9px] text-gray-400 mt-1.5">Loading the invoice…</div>;
  if (info === null) return null;

  const { invoice, po, sow, milestones } = info;
  return (
    <div className="mt-1.5 bg-gray-50 border border-gray-200 rounded px-2 py-1.5" onClick={e => e.stopPropagation()}>
      <div className="text-[9px] text-gray-600 mb-1.5">
        {invoice.supplier_name || 'Supplier'} invoice <span className="font-mono font-semibold">{invoice.invoice_number || '—'}</span>
        {invoice.total_excl_vat != null && <> · {m(invoice.total_excl_vat)} excl. VAT</>}
        {' '}on <span className="font-semibold">{po.po_number}</span>
        {invoice.sage_paid_at && <> · paid {formatDate(invoice.sage_paid_at.slice(0, 10))}</>}
      </div>

      {canDecide && (
        <div className="space-y-1.5">
          {milestones.length > 0 ? (
            <select value={milestoneId} onChange={e => { setMilestoneId(e.target.value); setError(''); }} disabled={busy}
              className="w-full text-[9px] border border-gray-300 rounded px-1.5 py-1 bg-white text-navy">
              <option value="">Choose the milestone on {sow?.reference} this releases…</option>
              {milestones.map(ms => (
                <option key={ms.id} value={ms.id}>
                  {ms.milestone_code}{ms.description ? ` · ${ms.description}` : ''}{ms.invoice_amount_excl ? ` · ${m(ms.invoice_amount_excl)}` : ''}{ms.status === 'Ready to bill' ? ' (already Ready to bill)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <div className="text-[9px] text-gray-500">{sow?.reference} has no milestones left to bill.</div>
          )}
          <input value={note} onChange={e => setNote(e.target.value)} disabled={busy}
            placeholder="Anything to add? (optional)"
            className="w-full text-[9px] border border-gray-300 rounded px-1.5 py-1 bg-white text-navy" />
          <div className="flex items-center gap-1.5">
            {milestones.length > 0 && (
              <button onClick={() => decide(true)} disabled={busy || !milestoneId}
                title={milestoneId ? 'Move this milestone to Ready to bill and close this' : 'Choose a milestone first'}
                className="px-2.5 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-30">
                {busy ? 'Saving…' : 'Move to Ready to bill'}
              </button>
            )}
            <button onClick={() => decide(false)} disabled={busy}
              title="This PO does not release a milestone. Close this."
              className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-100 disabled:opacity-30">
              No milestone needed
            </button>
          </div>
        </div>
      )}
      {error && <div className="mt-1 text-[9px] text-red-600">{error}</div>}
    </div>
  );
}
